/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/roles-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-roles-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/roles-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, organization_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, name
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/roles-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-roles-api-client.ts (API client)
 * - client/api/generated_schemas/roles.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-06-22T23:44:13.686Z
 * Source config: config/entities/roles-entity-config.ts
 * Public fields: 7/7 total fields
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RolesClient,
  RolesQueryOptions,
} from "../generated_clients/generated-roles-api-client.js";
import {
  RolesInsert,
  RolesUpdate,
  RolesFieldConfig,
} from "../../types/generated_schemas/generated-roles-schema.js";

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/roles_business_hooks/

// Query keys with enhanced caching strategy
export const rolesKeys = {
  all: ["roles"] as const,
  lists: () => [...rolesKeys.all, "list"] as const,
  list: (filters: Record<string, any>) =>
    [...rolesKeys.lists(), { filters }] as const,
  details: () => [...rolesKeys.all, "detail"] as const,
  detail: (id: string) => [...rolesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) =>
    [...rolesKeys.all, "search", query, fields] as const,
  filter: (filters: Record<string, any>) =>
    [...rolesKeys.all, "filter", filters] as const,
  sort: (sortBy: string, ascending: boolean) =>
    [...rolesKeys.all, "sort", sortBy, ascending] as const,
  count: (filters?: Record<string, any>) =>
    [...rolesKeys.all, "count", filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useRolesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new RolesClient();
};

/**
 * Hook to get all roles with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with roles data
 */
export const useRolesList = (options?: RolesQueryOptions) => {
  const client = useRolesClient();

  return useQuery({
    queryKey: rolesKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single role by ID
 *
 * @param id - The role ID
 * @returns React Query result with single role data
 */
export const useRoles = (id: string) => {
  const client = useRolesClient();

  return useQuery({
    queryKey: rolesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/roles
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/roles_business_hooks/
 */

/**
 * Hook to create a new role
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, name
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating role
 */
export const useCreateRoles = () => {
  const client = useRolesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RolesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
};

/**
 * Hook to update an existing role
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating role
 */
export const useUpdateRoles = () => {
  const client = useRolesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RolesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: rolesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a role
 *
 * @returns React Query mutation for deleting role
 */
export const useDeleteRoles = () => {
  const client = useRolesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
};

/**
 * Hook to count roles with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, organization_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useRolesCount = (
  filter?: Partial<Record<(typeof RolesFieldConfig.filterable)[number], any>>,
) => {
  const client = useRolesClient();

  return useQuery({
    queryKey: rolesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useRolesFieldConfig = () => {
  return RolesFieldConfig;
};
